"use client";

import { FileX, Clock, PhoneCall } from "lucide-react";
import { useRef } from "react";
import { MotionDiv, useInView } from "@/lib/framer";
import { Card, CardContent } from "./ui/card";
import { Container } from "./container";

interface PainPointCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  delay: number;
}

function PainPointCard({ icon, title, description, delay }: PainPointCardProps) {
  return (
    <MotionDiv initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay }}>
      <Card className="group border-golden-200 hover:border-golden-300 hover:shadow-golden-100 h-full border-2 bg-white/50 backdrop-blur-sm transition-all duration-300 hover:shadow-lg">
        <CardContent className="flex flex-col gap-4 p-8">
          <div className="bg-golden-100 w-fit rounded-full p-3">{icon}</div>
          <h3 className="text-foreground text-xl">
            <span className="from-golden-500 to-golden-600 bg-linear-to-r bg-clip-text text-transparent">{title}</span>
          </h3>
          <p className="text-muted-foreground">{description}</p>
        </CardContent>
      </Card>
    </MotionDiv>
  );
}

export function PainPointSection() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  const painPoints = [
    {
      icon: <PhoneCall className="text-golden-600 h-6 w-6" />,
      title: "Meeting Fatigue",
      description: "Daily standups interrupt focus time and drain team energy through constant context-switching.",
      delay: 0.2,
    },
    {
      icon: <Clock className="text-golden-600 h-6 w-6" />,
      title: "Lost Time",
      description: "Every minute in status meetings is a minute away from coding, slowing down your delivery timeline.",
      delay: 0.3,
    },
    {
      icon: <FileX className="text-golden-600 h-6 w-6" />,
      title: "Incomplete Updates",
      description: "Verbal updates miss critical code changes and context, leading to communication gaps.",
      delay: 0.4,
    },
  ];

  return (
    <Container className="from-golden-50 to-golden-100 bg-linear-to-br py-24">
      <MotionDiv
        ref={sectionRef}
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-16"
      >
        <div className="mx-auto max-w-prose text-center">
          <p className="text-golden-500 text-sm font-semibold tracking-wider uppercase">The Costly Meeting Crisis</p>
          <h2 className="mt-4 text-4xl tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
            Daily standups cost your team valuable time
          </h2>
          <p className="mt-6 text-lg text-gray-600">
            Traditional standups interrupt flow, waste development time, and often miss critical context. Here's why:
          </p>
        </div>

        <div className="mx-auto grid max-w-4xl gap-8 lg:grid-cols-3">
          {painPoints.map((point) => (
            <PainPointCard key={point.title} {...point} />
          ))}
        </div>
      </MotionDiv>
    </Container>
  );
}

export default PainPointSection;
